import React, { useState } from "react";
import { Table, Avatar, Tag, Dropdown, Button } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  FlagOutlined,
  FileTextOutlined,
  MoreOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { customStyles } from "./TaskCard";

const statusColors = {
  ACCEPTED: "#D1FAE5", // greenish → positive / approved
  OTW: "#FEF3C7", // yellow → in progress / on the way
  CANCELLED: "#FEE2E2", // red → negative / cancelled
  EXPIRED: "#F3F4F6", // gray → neutral / expired
};

const priorityColors = {
  HIGH: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
  },

  MEDIUM: {
    bg: "#FFF8E1",
    text: "#ED6C02",
    border: "#FFE0B2",
  },

  LOW: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
  },
};

const getPriorityLabel = (priority) => {
  if (priority === 0) return "HIGH";
  if (priority === 1 || priority === 2) return "MEDIUM";
  if (priority > 3) return "LOW";
  return "MEDIUM";
};

const getPriorityStyle = (priority) => {
  const label = getPriorityLabel(priority);

  return (
    priorityColors[label] || {
      bg: "#F3F4F6",
      text: "#374151",
      border: "#E5E7EB",
    }
  );
};

const statusOrder = ["OPEN", "IN_PROGRESS", "COMPLETED", "BLOCKED"];

const statusDotColor = (status) => {
  switch (status) {
    case "OPEN":
      return "#8B5CF6";
    case "IN_PROGRESS":
      return "#3B82F6";
    case "BLOCKED":
      return "#F59E0B";
    case "COMPLETED":
      return "#10B981";
    default:
      return "#9CA3AF";
  }
};

const formatStatus = (status) =>
  status
    .replace("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

/* ================= STATUS SECTION ================= */
const StatusSection = ({
  status,
  tasks,
  onTaskClick,
  selectedRowKeys,
  setSelectedRowKeys,
  onEditTask,
  onDelete,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const columns = [
    {
      title: (
        <span className="table-header">
          <FileTextOutlined /> Task Name
        </span>
      ),
      dataIndex: "title",
      width: 220,
      render: (title) => (
        <div
          className="task-title"
          style={{
            fontSize: 12,
          }}
        >
          {title}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 50,
      render: (desc) => (
        <div
          className="project-desc"
          style={{
            color: "#374151",
            fontSize: 12,
            margin: 0,
            padding: 0,
            width: 140,
          }}
          dangerouslySetInnerHTML={{
            __html: desc || "<p>No description provided</p>",
          }}
        />
      ),
    },
    {
      title: (
        <span className="table-header">
          <CalendarOutlined /> Due Date
        </span>
      ),
      dataIndex: "expectedCompletionDate",
      width: 140,
      render: (date) => date || "—",
    },
    {
      title: (
        <span className="table-header">
          <UserOutlined /> Assignee
        </span>
      ),
      dataIndex: "assigneeName",
      width: 170,
      render: (name) =>
        name ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontFamily: "Inter",
            }}
          >
            <Avatar size="small">
              {name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </Avatar>
            <span style={{ fontFamily: "Inter" }}>{name}</span>
          </div>
        ) : (
          <span style={{ color: "#9ca3af" }}>Unassigned</span>
        ),
    },
    {
      title: (
        <span className="table-header">
          <FlagOutlined /> Assignment Status
        </span>
      ),
      dataIndex: "assignmentStatus",
      width: 120,
      render: (status) => (
        <span
          className="task-category"
          style={{
            background: statusColors[status] || "#F3F4F6",
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: (
        <span className="table-header">
          <FlagOutlined /> Priority
        </span>
      ),
      dataIndex: "priority",
      width: 120,
      render: (priority) => {
        const priorityStyle = getPriorityStyle(priority);
        return (
          <span
            className="task-priority"
            style={{
              ...customStyles,
              backgroundColor: priorityStyle.bg,
              color: priorityStyle.text,
              border: `1px solid ${priorityStyle.border}`,
            }}
          >
            {getPriorityLabel(priority)}
          </span>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 60,
      align: "right",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Edit",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              onEditTask(record);
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Delete",
            danger: true,
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              onDelete?.(record);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="topRight">
            <MoreOutlined
              style={{
                fontSize: 16,
                cursor: "pointer",
                color: "#6b7280",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <div
        className="column-header list-section-header"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="column-title">
          <span
            className="status-dot"
            style={{ background: statusDotColor(status) }}
          />
          <h5 className="fw-bold mb-0 pb-0 text-xxs">{formatStatus(status)}</h5>
          <span className="task-count">{tasks.length}</span>
        </div>

        {collapsed ? (
          <PlusOutlined className="collapse-icon" />
        ) : (
          <MinusOutlined className="collapse-icon" />
        )}
      </div>

      <div className="list-section">
        {!collapsed && (
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            pagination={false}
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: () => onTaskClick?.(record),
            })}
            expandable={{
              expandedRowRender: (record) => {
                const metaItems = [
                  {
                    icon: <UserOutlined />,
                    label: "CreatedBy",
                    value: record.createdByName || "-",
                  },
                  {
                    icon: <UserOutlined />,
                    label: "Assignee",
                    value: record.assigneeName || "Unassigned",
                  },
                  {
                    icon: <CalendarOutlined />,
                    label: "Due Date",
                    value: record.expectedCompletionDate || "—",
                  },
                  {
                    icon: <FlagOutlined />,
                    label: "Priority",
                    value: getPriorityLabel(record.priority),
                  },
                  {
                    icon: <FlagOutlined />,
                    label: "Assignment Status",
                    value: record.assignmentStatus || "—",
                  },
                ];

                const MetaRow = ({ icon, label, value }) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flex: 1,
                    }}
                  >
                    {icon}
                    <strong>{label}:</strong>
                    <span>{value}</span>
                  </div>
                );

                // Split meta items into pairs for two columns
                const rows = [];
                for (let i = 0; i < metaItems.length; i += 2) {
                  rows.push(metaItems.slice(i, i + 2));
                }

                return (
                  <div style={{ padding: 10, background: "#f9fafb" }}>
                    {rows.map((pair, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          gap: 40, // spacing between columns
                          marginBottom: 6,
                        }}
                      >
                        {pair.map((item, index) => (
                          <MetaRow
                            key={index}
                            icon={item.icon}
                            label={item.label}
                            value={item.value}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                );
              },
              rowExpandable: (record) => true,
            }}
            className="custom-table"
            size="small"
          />
        )}
      </div>
    </>
  );
};

/* main list view */
const ListView = ({ tasks, onTaskClick, onEditTask, onDelete }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const clearSelection = () => setSelectedRowKeys([]);

  return (
    <div className="list-view-container">
      {statusOrder.map((status) => {
        const filtered = tasks.filter((task) => task.status === status);
        if (!filtered.length) return null;

        return (
          <StatusSection
            key={status}
            status={status}
            tasks={filtered}
            onTaskClick={onTaskClick}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            onEditTask={onEditTask}
            onDelete={onDelete}
          />
        );
      })}
    </div>
  );
};

export default ListView;
